"use client";
import ResourceIcon from "@/app/components/logos/resources/ResourceIcon";
import SubjectIcon from "@/app/components/logos/SubjectIcon";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Group, Skeleton, Stack, Text, Title } from "@mantine/core";
import { KeyboardArrowRightRounded } from "@mui/icons-material";
import { Resources } from "@tsTypes/subject-schema";
import { buildFullPath, buildSubjectSlug } from "@utils/routing";
import Link from "next/link";
import { Fragment, Suspense } from "react";
import classes from "./SubjectHeader.module.css";
import ResourceTabs from "./tabs/ResourceTabs";
import SubTopicTabs from "./tabs/SubTopicTabs";
import { default as SubTopicThemeTabs } from "./tabs/SubTopicThemeTabs";

export default function SubjectHeader({
  type,
  title,
  description,
  subjectName,
  topicDocumentId,
  relevantSubTopicDocumentIds,
  subTopicDocumentId,
  resource,
  subTopicHasSubThemes,
}: subjectHeaderProps) {
  const subjectNameLower = subjectName.toLowerCase();
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const breadCrumbIcons = getBreadCrumbMeta(subjectName, type, resource);

  return (
    <Stack
      className={classes.headerContainer}
      style={
        {
          "--sh-c6": `var(--mantine-color-${subjectColorIndex}-6)`,
          "--sh-c9": `var(--mantine-color-${subjectColorIndex}-9)`,
        } as React.CSSProperties
      }
    >
      <div className={classes.backgroundOverflowContainer}>
        <div className={classes.backgroundIconWrapper}>
          {[...Array(resource ? 6 : 12)].map((_, i) => {
            return resource ? (
              <Fragment key={"background-icons-" + i}>
                <ResourceIcon
                  resource={resource}
                  className={classes.headerIcon}
                />
                <SubjectIcon
                  className={classes.headerIcon}
                  subjectColorIndex={subjectColorIndex}
                />
              </Fragment>
            ) : (
              <SubjectIcon
                key={"subject-icon-" + i}
                className={classes.headerIcon}
                subjectColorIndex={subjectColorIndex}
              />
            );
          })}
        </div>
      </div>
      <Group className={classes.breadcrumbs}>
        {type !== "Subject" && (
          <Link
            href={buildSubjectSlug(subjectName)}
            className={classes.breadcrumbLink}
            title={`Back to IB ${subjectName}`}
            aria-label={`Back to IB ${subjectName}`}
          >
            <SubjectIcon subjectColorIndex={subjectColorIndex} />
          </Link>
        )}
        {breadCrumbIcons &&
          breadCrumbIcons.map((i) => (
            <Fragment key={"breadcrumb-" + i.href}>
              <KeyboardArrowRightRounded
                style={{ width: 25, height: 25 }}
                htmlColor="white"
              />
              <Link
                href={i.href}
                className={classes.breadcrumbLink}
                title={i.text}
                aria-label={i.text}
              >
                {i.text}
              </Link>
            </Fragment>
          ))}
      </Group>
      <div className={classes.contentWrapper}>
        <Title order={1} className={classes.title} id="page-title">
          {title}
        </Title>
        <Text className={classes.description} id="page-description">
          {description}
        </Text>
      </div>
      {
        ["Subject", "Resource"].includes(type) &&
          !["topic tests", "mock exams", "test builder", "test taker"].includes(
            resource ?? ""
          ) && (
            <ResourceTabs
              subjectNameLower={subjectNameLower}
              currentResource={resource ?? "all"}
            />
          )
      }
      {
        ["notes", "flashcards", "questions", "definitions"].includes(
          resource ?? ""
        ) &&
          relevantSubTopicDocumentIds && (
            <SubTopicTabs
              subjectNameLower={subjectNameLower}
              currentResource={resource ?? "all"}
              currentSubTopicId={subTopicDocumentId}
              topicDocumentId={topicDocumentId}
              relevantSubTopicDocumentIds={relevantSubTopicDocumentIds}
            />
          )
      }
      {
        resource === "questions" && subTopicHasSubThemes && (
          <Suspense
            fallback={<Skeleton height={30} width="100%" animate={true} />}
          >
            <SubTopicThemeTabs
              subjectNameLower={subjectNameLower}
              currentResource={resource ?? "all"}
              topicDocumentId={topicDocumentId}
              currentSubTopicId={subTopicDocumentId}
            />
          </Suspense>
        )
      }
    </Stack>
  );
}

const getBreadCrumbMeta = (
  subjectName: string,
  type: PageHeaderTypes,
  resource: Resources | undefined
) => {
  if (
    !resource ||
    type === "Subject" ||
    (type === "Resource" &&
      !["topic tests", "mock exams", "test builder", "test taker"].includes(
        resource
      ))
  )
    return;
  let adjustedResouce: Resources = resource;
  switch (resource) {
    case "test builder":
    case "mock exams":
    case "topic tests":
    case "test taker":
      adjustedResouce = "exam tests";
    default:
      break;
  }
  const icons = [
    {
      href: buildFullPath({
        subject: subjectName,
        resource: adjustedResouce,
      }),
      text: [
        "test builder",
        "mock exams",
        "topic tests",
        "test taker",
      ].includes(resource)
        ? "tests"
        : resource,
    },
  ];
  if (type === "Topic" && resource === "topic tests") {
    icons.push({
      href: buildFullPath({
        subject: subjectName,
        resource: "topic tests",
      }),
      text: resource,
    });
  }

  return icons;
};

type subjectHeaderProps =
  | {
      type: Extract<PageHeaderTypes, "Subject">;
      title: string;
      description: string;
      subjectName: string;
      topicDocumentId?: undefined;
      relevantSubTopicDocumentIds?: undefined;
      resource?: undefined;
      subTopicDocumentId?: undefined;
      subTopicHasSubThemes?: undefined;
    }
  | {
      type: Extract<PageHeaderTypes, "Resource">;
      title: string;
      description: string;
      subjectName: string;
      topicDocumentId?: undefined;
      relevantSubTopicDocumentIds?: undefined;
      resource: Resources;
      subTopicHasSubThemes?: undefined;
      subTopicDocumentId?: undefined;
    }
  | {
      type: Extract<PageHeaderTypes, "Topic">;
      title: string;
      description: string;
      subjectName: string;
      topicDocumentId: string;
      relevantSubTopicDocumentIds: string[];
      resource: Extract<
        Resources,
        | "videos"
        | "notes"
        | "flashcards"
        | "questions"
        | "definitions"
        | "cram sheets"
        | "topic tests"
      >;
      subTopicDocumentId?: undefined;
      subTopicHasSubThemes?: undefined;
    }
  | {
      type: Extract<PageHeaderTypes, "Subtopic">;
      title: string;
      description: string;
      subjectName: string;
      topicDocumentId: string;
      relevantSubTopicDocumentIds: string[];
      resource: Extract<
        Resources,
        "notes" | "flashcards" | "questions" | "definitions"
      >;
      subTopicDocumentId: string;
      subTopicHasSubThemes: boolean;
    };

export type PageHeaderTypes = "Subject" | "Resource" | "Topic" | "Subtopic";
